// next/fontFlag.ts -- the typeface flag (decision G-1 of the modern-UI commission; issues #2293 and #2297). RULED by the operator
// (q-1022-g1-typeface-allow-one-self-hosted-face): the self-hosted Plex face is the DEFAULT face of the next shell; `?font=system` is the escape.
//
//   neither, or an unknown value -> plex: the default face
//   ?font=system    -> <html data-font="system"> for this load, and remembered (localStorage va.font = system): the escape, no font file is requested
//   ?font=plex      -> <html data-font="plex"> for this load, and remembered (va.font = plex)
//   a stored value  -> applied when the URL says nothing (a load that did not choose never rewrites the stored choice)
//
// Only html[data-font="system"] styles anything (next/next.css points the shell's two font tokens back at the system stack): the document's own
// tokens (the generated styles/est-tokens.css) lead with the self-hosted face declared in styles/fonts-candidate.css, so `plex` adds no rule. Under
// the escape nothing in use names the family, so a browser never asks for a font file; only the next shell reads those tokens, so the classic
// shells draw exactly as before. The decision is a PURE function (setpoint-evals/ui-typeface-policy SE-03 pins it, with must-fail
// controls; SE-05 renders it in a browser and reads the requests); the reader below is the only code that touches the URL, localStorage and the
// document, each guarded (storage can be absent or throw in private windows and previews).
//
// The module applies the flag when it is imported: main.tsx imports it before the first render, so the attribute is on <html> before
// the first paint and no frame is drawn under the wrong face. It imports nothing.

export type FontChoice = 'system' | 'plex';

export const FONT_STORAGE_KEY = 'va.font';

const CHOICES: ReadonlySet<string> = new Set(['system', 'plex']);

export interface FontDecision {
  font: FontChoice;
  /** The value to remember: the URL's choice, or null when the URL chose nothing (a stored choice is never rewritten). */
  persist: FontChoice | null;
}

/** Pure: the query string and the stored value in, the font and the storage action out. The URL wins; Plex is the default. */
export function resolveFont(input: { search: string; stored: string | null }): FontDecision {
  const raw = new URLSearchParams(input.search).get('font')?.trim().toLowerCase() ?? null;
  if (raw !== null && CHOICES.has(raw)) return { font: raw as FontChoice, persist: raw as FontChoice };
  const stored = input.stored?.trim().toLowerCase() ?? null;
  if (stored !== null && CHOICES.has(stored)) return { font: stored as FontChoice, persist: null };
  return { font: 'plex', persist: null };
}

/** Reads the URL and the stored choice, remembers a choice the URL made, and stamps <html data-font>. Inert outside a browser. */
export function applyFontFlag(): FontChoice {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 'plex';
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(FONT_STORAGE_KEY);
  } catch {
    /* storage unavailable: the URL, or the default, decides this load */
  }
  const d = resolveFont({ search: window.location.search, stored });
  if (d.persist !== null) {
    try {
      localStorage.setItem(FONT_STORAGE_KEY, d.persist);
    } catch {
      /* storage unavailable: the choice holds for this load only */
    }
  }
  document.documentElement.dataset.font = d.font;
  return d.font;
}

applyFontFlag();
